import { useCallback, useContext, useEffect, useState } from 'react'
import { useLocation, useNavigate, useSearchParams } from 'react-router'
import { ChevronRightOutlined } from '@mui/icons-material'
import { Button, ButtonBase, CircularProgress, Grid, Typography } from '@mui/material'
import { Lottie } from 'lottie-react'
import { useSnackbar } from 'notistack'

import confirmationAnimation from '../../animations/alert-confirmation.json'
import Header from '../../components/alert-acknowledgement/header'
import LocationCard from '../../components/alert-acknowledgement/location-card'
import NeedHelpModal from '../../components/need-help-modal'
import ScreenContainer from '../../components/screen'
import { AuthenticationContext } from '../../providers/auth-provider'
import Api from '../../utils/api'
import usePauseDevice from '../../utils/hooks/usePauseDevice'

import { getHelpCategory, helpContent } from './help-content'
import useStyles from './index.styles'
import PauseDialog from './pause-dialog'
import PauseDuration from './pause-duration'

const AlertSubmit = () => {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const { state } = useLocation()
  const { data, email, phone, code, fromLogin, deviceId, supportLevel } = state || {}

  const classes = useStyles

  const { enqueueSnackbar } = useSnackbar()
  const { isAuthenticated } = useContext(AuthenticationContext)

  const [isAuthenticatingAlert, setIsAuthenticatingAlert] = useState<boolean>(false)
  const [isLoadingDevice, setIsLoadingDevice] = useState<boolean>(true)
  const [loadingDeviceError, setLoadingDeviceError] = useState<boolean>(false)
  const [device, setDevice] = useState<IDeviceType | undefined>(undefined)

  const [openPauseDialog, setOpenPauseDialog] = useState<boolean>(false)
  const [showNeedHelpModal, setShowNeedHelpModal] = useState<boolean>(false)

  const acknowledgeType = searchParams.get('type')
  const helpCategory = getHelpCategory(data?.reaction || '')
  const helpSteps = helpCategory !== 'none' ? helpContent[helpCategory] : []
  const { isPaused, willBePaused, selectedTimezone } = usePauseDevice({
    device,
  })
  const alreadyResponded = !!data?.type // Alert has already been responded to if type is set in data

  useEffect(() => {
    if (!data) navigate('/')
  }, [data, navigate])

  const fetchDevice = useCallback(async () => {
    setIsLoadingDevice(true)
    let alertDeviceId = deviceId
    if (!alertDeviceId) {
      const res = await Api.post('/api/v2/alarms/response/authenticate', {
        code,
        email,
        phone,
      })
      alertDeviceId = res.body.nodeId
    }
    const resDevice = await Api.get(`/api/v2/protect/devices/${alertDeviceId}`)
    if (resDevice.ok) {
      setDevice(resDevice.body)
      if (fromLogin) setOpenPauseDialog(true)
    } else {
      setLoadingDeviceError(true)
      enqueueSnackbar('Could not fetch device', { variant: 'error' })
    }
    setIsLoadingDevice(false)
  }, [code, deviceId, email, enqueueSnackbar, fromLogin, phone])

  // If user is authenticated and acknowledge type is "test" and we have the required acknowledgement auth information, fetch the device
  useEffect(() => {
    if (isAuthenticated && acknowledgeType === 'test' && code) fetchDevice()
  }, [acknowledgeType, code, fetchDevice, isAuthenticated])

  if (!data) return null

  const acknowledgeTitle = acknowledgeType === 'ack' ? 'Acknowledged' : 'Ignored'

  const onPauseButtonClick = async () => {
    // User is already authenticated, show the pause dialog
    if (acknowledgeType === 'test' && isAuthenticated && !isPaused && device) {
      setOpenPauseDialog(true)
      return
    }

    // User is not authenticated, send user to login screen, send them back here when they are done logging in
    // First authenticate the alert, get the node id, and save it for when they return
    setIsAuthenticatingAlert(true)
    const res = await Api.post('/api/v2/alarms/response/authenticate', {
      code,
      email,
      phone,
    })

    setIsAuthenticatingAlert(false)
    if (!res.ok) {
      enqueueSnackbar('Could not authenticate alert for pausing device', { variant: 'error' })
      return
    }
    navigate('/login', { state: { alertAckData: data, alertAckResponse: acknowledgeType, alertAckCode: code, deviceId: res.body.nodeId } })
  }

  return (
    <ScreenContainer header={<Header variant="blue" />}>
      <Grid container={true} sx={classes.container}>
        <Grid container={true} size={12} sx={classes.mainContainerWithMargin(acknowledgeType || '')}>
          <Grid container={true} size={12} sx={classes.headerContainer}>
            {acknowledgeType === 'ack' && (
              <Lottie src={confirmationAnimation} autoplay={true} loop={false} style={classes.confirmationAnimation} data-testid="confirmation-animation" />
            )}
            <Typography variant="h3" sx={classes.headerTitle}>
              Alert {acknowledgeTitle}
            </Typography>
          </Grid>

          {(acknowledgeType === 'ack' || alreadyResponded) && (
            <LocationCard deviceLocation={data?.metadata?.device_location} deviceLocationNote={data?.metadata?.device_location_note} />
          )}

          {!alreadyResponded && helpSteps.length > 0 && acknowledgeType === 'ack' && (
            <Grid container={true} size={12} sx={classes.helpStepsContainer}>
              <Typography variant="body2" color="text.secondary" sx={classes.helpStepsTitle}>
                What to Do Now
              </Typography>
              <ul style={classes.bulletList}>
                {helpSteps.map((step, index) => (
                  <Typography variant="body2" component="li" color="text.secondary" key={index}>
                    {step}
                  </Typography>
                ))}
              </ul>
            </Grid>
          )}

          {((!alreadyResponded && acknowledgeType === 'test' && !isAuthenticated) ||
            (!alreadyResponded && acknowledgeType === 'test' && isAuthenticated && !isLoadingDevice && !loadingDeviceError && !isPaused && !willBePaused)) && (
            <ButtonBase onClick={onPauseButtonClick} disabled={isAuthenticatingAlert}>
              <Grid container={true} sx={classes.pauseButtonContainer} spacing={2}>
                <Grid container={true} sx={classes.pauseButtonContent}>
                  <Typography variant="body1">Pause this device</Typography>
                  <Typography variant="body2" color="text.secondary">
                    While paused, this device will stop sending notifications to all recipients.
                  </Typography>
                </Grid>
                {isAuthenticatingAlert ? (
                  <CircularProgress size="16px" aria-label="Sending verification code..." />
                ) : (
                  <ChevronRightOutlined htmlColor="text.primary" />
                )}
              </Grid>
            </ButtonBase>
          )}

          {!alreadyResponded && acknowledgeType === 'test' && isAuthenticated && !isPaused && !willBePaused && (
            <>
              {isLoadingDevice ? (
                <Grid container sx={classes.loadingContainer}>
                  <CircularProgress size={24} />
                </Grid>
              ) : (
                <PauseDialog
                  device={device}
                  selectedTimezone={selectedTimezone}
                  openPauseDialog={openPauseDialog}
                  setOpenPauseDialog={setOpenPauseDialog}
                  setDevice={setDevice}
                />
              )}
            </>
          )}
          {!alreadyResponded && acknowledgeType === 'test' && (isPaused || willBePaused) && device && (
            <PauseDuration device={device} timezone={selectedTimezone} />
          )}

          <Grid sx={classes.needHelpButtonContainer}>
            <Button
              variant="contained"
              size="large"
              disableElevation={true}
              color="secondary"
              onClick={() => {
                setShowNeedHelpModal(true)
              }}
            >
              Need more help?
            </Button>
          </Grid>
        </Grid>

        <NeedHelpModal isOpen={showNeedHelpModal} onClose={() => setShowNeedHelpModal(false)} helpCategory={helpCategory} supportLevel={supportLevel} />
      </Grid>
    </ScreenContainer>
  )
}

export default AlertSubmit
